import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../redux/store';
import { ToggleColorModeButton } from '../../../theme/ToggleColorModeButton';
import { forgotPasswordSchema } from '../validation';
import { forgotPassword } from '../store';
import { ForgotPasswordFormData } from './types';
import {
  AuthContainer,
  FormContainer,
  FormCard,
  Title,
  Subtitle,
  ButtonWrapper,
} from '../styles';
import { ThemeProvider } from 'styled-components';
import AuthBackground from '../LoginPage/AuthBackground';
import ForgotPasswordForm from './ForgotPasswordForm';
import SuccessView from './SuccessView';

const ForgotPasswordPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useSelector((state: RootState) => state.theme);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordFormData>({
    // @ts-expect-error - yup resolver type mismatch with react-hook-form
    resolver: yupResolver(forgotPasswordSchema),
    mode: 'onChange',
  });

  const emailValue = watch('email');

  const onSubmit = async (data: ForgotPasswordFormData): Promise<void> => {
    const result = await dispatch(forgotPassword(data.email.trim()));
    if (forgotPassword.fulfilled.match(result)) {
      setIsSubmitted(true);
    }
  };

  // @ts-expect-error - react-hook-form type inference issue with yup resolver
  const handleFormSubmit = handleSubmit(onSubmit);

  const handleBackToLogin = (): void => {
    navigate('/login');
  };

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'dark' | 'light' };

  return (
    <ThemeProvider theme={theme}>
      <AuthContainer>
        <AuthBackground isDarkMode={isDarkMode} />
        
        <ToggleColorModeButton
          width="24px"
          height="24px"
          iconButtonProps={{
            position: 'absolute',
            top: '5',
            right: '5',
            zIndex: 10,
          }}
        />

        <FormContainer>
          <FormCard>
            {!isSubmitted ? (
              <>
                <Title>Forgot Password</Title>
                <Subtitle>
                  Enter your email address and we'll send you a code to reset your password.
                </Subtitle>
                
                <ForgotPasswordForm
                  control={control as any}
                  errors={errors}
                  onSubmit={handleFormSubmit}
                  isLoading={false}
                />
              </>
            ) : (
              <SuccessView email={emailValue || ''} />
            )}
            
            <ButtonWrapper>
              <Button
                icon={<FontAwesomeIcon icon={faArrowLeft} />}
                type="link"
                onClick={handleBackToLogin}
                block
                style={{
                  marginTop: '16px',
                }}
              >
                Back to Login
              </Button>
            </ButtonWrapper>
          </FormCard>
        </FormContainer>
      </AuthContainer>
    </ThemeProvider>
  );
};

export default ForgotPasswordPage;
